// TODO: TBD use MUI validation or custom
import React, { useContext, useRef, useState } from 'react';
import { Auth } from 'aws-amplify';
import { Trans, useTranslation } from 'react-i18next';
import {
  Typography,
  Container,
  Grid,
  Checkbox,
  FormControlLabel,
  FormGroup,
  useTheme,
} from '@mui/material';
import { di } from '../../../state/inversify.config';
import { LoginDomainService } from '../loginDomainService';
import { PasswordGuidelines } from '../../signUp/passwordGuidelines';
import { checkPasswordStrength } from './validation.helper';
import { LegalContent } from '../../../components/legalLinks/legal';
import { UserNotificationContext } from '../../../components/userNotification/userNotificationContext';
import { UserNotificationType } from '../../../components/userNotification/userNotificationType.enum';
import { AlertDialog } from '../../../components/alertDialog/alertDialog';
import { PasswordTextInput } from '../../../components/passwordTextInput/passwordTextInput';
import { Button } from '../../../components/button/button';
import { Spinner } from '../../../components/spinner/spinner';

interface ChangePasswordOnFirstLoginProps {
  username: string;
  password: string;
}
export function ChangePasswordOnFirstLogin({
  username,
  password,
}: ChangePasswordOnFirstLoginProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { addUserNotification } = useContext(UserNotificationContext);

  const loginDomainService = di.get<LoginDomainService>(LoginDomainService);

  const [inputs, setInputs] = useState({
    newPassword: '',
    confirmNewPassword: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [passwordUpdated, setPasswordUpdated] = useState(false);
  const [authenticatedUser, setAuthenticatedUser] = useState('');
  const [showPasswordError, setShowPasswordError] = useState(false);
  const [legalType, setLegalType] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [legalHeader, setLegalHeader] = useState('');
  const [showLegalContentModal, setShowLegalContentModal] = useState(false);
  const parentRef = useRef(null);

  const resetPassword = () => {
    if (!accepted) {
      addUserNotification(
        UserNotificationType.ERROR,
        t('LEGAL_ERRORS').toString(),
        UserNotificationType.ERROR,
      );
      return;
    }
    if (inputs.newPassword !== inputs.confirmNewPassword) {
      addUserNotification(
        UserNotificationType.ERROR,
        t('NEW_PASSWORD_DOES_NOT_MATCH').toString(),
        UserNotificationType.ERROR,
      );
      return;
    }
    setIsLoading(true);
    Auth.signIn(username, password)
      .then((user) => {
        setAuthenticatedUser(user.username);
        if (
          user.challengeName === 'NEW_PASSWORD_REQUIRED' ||
          user.challengeName === 'FORCE_CHANGE_PASSWORD'
        ) {
          Auth.completeNewPassword(user, inputs.newPassword)
            .then(() => {
              setPasswordUpdated(true);
              setIsLoading(false);
              login();
            })
            .catch((err) => {
              addUserNotification(
                UserNotificationType.ERROR,
                err.message,
                UserNotificationType.ERROR,
              );
            });
        }
      })
      .catch((_error) => {
        addUserNotification(
          UserNotificationType.ERROR,
          _error.message,
          UserNotificationType.ERROR,
        );
      });
  };

  const login = () => {
    setIsLoading(true);
    loginDomainService.dispatchLogin({
      email: username,
      password: inputs.newPassword,
    });
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (name === 'newPassword') {
      setShowPasswordError(true);
      const validPassword = checkPasswordStrength(value);
      if (validPassword) {
        setShowPasswordError(false);
      }
    }
    setInputs((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
  };

  const handleTermsAndPrivacyPolicyChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { checked } = event.target;
    setAccepted(checked);
  };

  const handleLinkClick = (e: any) => {
    e.preventDefault();
    if (e.target.tagName === 'A') {
      if (e.target.getAttribute('href') === '/terms') {
        setShowLegalContentModal(true);
        setLegalType('terms');
        setLegalHeader(t('TERMS_OF_USE_TITLE').toString());
      } else if (e.target.getAttribute('href') === '/privacy') {
        setShowLegalContentModal(true);
        setLegalType('privacy');
        setLegalHeader(t('PRIVACY_POLICY_HEADER').toString());
      }
    }
  };

  return (
    <Container>
      {isLoading ? (
        <Spinner open={isLoading} />
      ) : (
        <>
          {!passwordUpdated && (
            <Grid>
              <Typography
                component="h1"
                variant="h4"
                sx={{ textAlign: 'center' }}
              >
                {t('RESET_PASSWORD')}
              </Typography>
              <p>{t('RESET_TEMP_PASSWORD')}</p>

              <PasswordTextInput
                label={t('NEW_PASSWORD')}
                name="newPassword"
                fullWidth
                required
                onChange={handleInputChange}
                value={inputs.newPassword}
              />
              <PasswordTextInput
                label={t('CONFIRM_NEW_PASSWORD')}
                name="confirmNewPassword"
                autoComplete="new-password"
                fullWidth
                required
                onChange={handleInputChange}
                value={inputs.confirmNewPassword}
              />
              <PasswordGuidelines showPasswordError={showPasswordError} />
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      title="termsAndPrivacyPolicy"
                      onChange={handleTermsAndPrivacyPolicyChange}
                      name="termsAndPrivacyPolicy"
                      sx={{ color: theme.palette.text.primary }}
                    />
                  }
                  label={
                    <Typography
                      ref={parentRef}
                      onClick={handleLinkClick}
                      variant="body2"
                    >
                      <Trans i18nKey="AGREE_AND_READ_TERMS_AND_CONDITION">
                        <a href="/terms">placeholder_text</a>
                        <a href="/privacy">placeholder_text</a>
                      </Trans>
                    </Typography>
                  }
                />
              </FormGroup>
              <Grid textAlign="right" sx={{ mt: 2, mb: 1 }}>
                <Button
                  color="secondary"
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  onClick={resetPassword}
                  disabled={
                    inputs.newPassword === '' ||
                    inputs.confirmNewPassword === ''
                  }
                  label={t('common.generic.GENERIC_NAVIGATION_NEXT')}
                />
              </Grid>
            </Grid>
          )}
        </>
      )}
      <AlertDialog
        title={legalHeader}
        setOpen={setShowLegalContentModal}
        open={showLegalContentModal}
        content={<LegalContent legal={legalType} />}
      />
    </Container>
  );
}
